<!-- profile-data.component.html -->
<div *ngIf="isLoading" class="mx-auto max-w-screen-xl p-4 lg:p-8">
  <div class="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden w-full">
    <app-loader [rows]="10"></app-loader>
  </div>
</div>

<div
  *ngIf="!isLoading && user && !isAuthor"
  class="mx-auto max-w-screen-xl p-4 lg:p-8 flex flex-col md:flex-row justify-center md:justify-start relative"
>
  <div class="flex flex-col md:flex-row items-center gap-x-4 gap-y-2 w-full">
    <div class="flex justify-center md:justify-start w-full md:w-1/4">
      <div
        class="relative w-32 h-40 sm:w-40 sm:h-48 overflow-hidden rounded border-4 border-gray-200 shadow-xl"
      >
        <img
          class="object-cover w-full h-full"
          [attr.src]="user.profile_picture || 'assets/profile.png'"
          alt="Profile Picture"
        />
      </div>
    </div>
    <div
      class="w-full flex flex-col sm:flex-row md:flex-col md:flex-grow justify-center md:justify-start gap-x-4 gap-y-2"
    >
      <div class="w-full md:border-r pr-7">
        <div class="text-center">
          <strong class="block text-xl sm:text-2xl font-bold mb-2"
            >{{ user.first_name }} {{ user.last_name }}</strong
          >
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          <ng-container *ngIf="user.institution">
            <div class="break-words">
              <strong class="block text-base sm:text-lg font-semibold mb-2 text-gray-700"
                >Institution</strong
              >
              <p class="text-sm sm:text-base text-gray-900">
                {{ user.institution }}
              </p>
            </div>
          </ng-container>
          <ng-container *ngIf="user.website">
            <div class="break-words">
              <strong class="block text-base sm:text-lg font-semibold mb-2 text-gray-700"
                >Website</strong
              >
              <p class="text-sm sm:text-base text-gray-900">
                <a href="{{ user.website }}" class="text-blue-500 hover:underline">{{
                  user.website
                }}</a>
              </p>
            </div>
          </ng-container>
          <ng-container *ngIf="user.investigation_camp">
            <div class="break-words">
              <strong class="block text-base sm:text-lg font-semibold mb-2 text-gray-700"
                >Research Field</strong
              >
              <p class="text-sm sm:text-base text-gray-900">{{ user.investigation_camp }}</p>
            </div>
          </ng-container>
          <ng-container *ngIf="user.interests">
            <div class="break-words">
              <strong class="block text-base sm:text-lg font-semibold mb-2 text-gray-700"
                >Research Interests</strong
              >
              <p class="text-sm sm:text-base text-gray-900">{{ user.interests }}</p>
            </div>
          </ng-container>
          <ng-container *ngIf="user.email_institution">
            <div class="break-words">
              <strong class="block text-base sm:text-lg font-semibold mb-2 text-gray-700"
                >Institutional Email</strong
              >
              <p class="text-sm sm:text-base text-gray-900">
                {{ user.email_institution }}
              </p>
            </div>
          </ng-container>
        </div>
        <div *ngIf="shouldShowLoggedInMessage" class="text-justify mt-2">
          <p class="text-gray-600">
            Welcome {{ user.first_name }}! It's great to see you here. By completing your profile,
            you can better showcase your professional journey and achievements.
          </p>
          <p class="text-gray-600 mt-2">
            Adding details such as your institution, website, and research interests can help others
            in your field find and connect with you more easily (click on "Edit Profile").
          </p>
          <p class="text-gray-600 mt-2">
            Remember, every bit of information you add makes your profile richer and more impactful.
            Take a few moments to fill in these details and let your professional persona shine!
          </p>
        </div>
        <div *ngIf="shouldShowLoggedOutMessage" class="text-justify mt-6">
          <p class="text-gray-600">
            Welcome to {{ user.first_name }} {{ user.last_name }}'s profile! Currently, this profile
            doesn't have detailed information filled out yet.
          </p>
          <p class="text-gray-600 mt-2">
            Encourage {{ user.first_name }} to complete their profile to better showcase their
            professional journey and achievements.
          </p>
          <p class="text-gray-600 mt-2">
            Detailed profiles help in making professional connections and collaborations more
            effective. If you know {{ user.first_name }}, feel free to suggest them to add more
            details here.
          </p>
        </div>
      </div>
    </div>
    <div
      *ngIf="user.scopus_id; else noScopusId"
      class="w-full md:w-2/5 flex flex-col justify-center items-center gap-x-4 gap-y-1 mt-2"
    >
      <div class="w-full">
        <div>
          <strong class="block text-md sm:text-lg font-bold mb-1">Citations</strong>
          <p>{{ scopusData.citations }}</p>
          <strong class="block text-md sm:text-lg font-bold mt-4 mb-1">SCOPUS ID</strong>
          <p>{{ user.scopus_id }}</p>
          <strong class="block text-md sm:text-lg font-bold mt-4 mb-1">Articles</strong>
          <p>{{ scopusData.articles }}</p>
        </div>
      </div>
    </div>
    <ng-template #noScopusId>
      <div
        *ngIf="isOwnProfile"
        class="w-full md:w-2/5 flex flex-col justify-center items-center gap-x-4 gap-y-1"
      >
        <div class="w-full px-3 text-center">
          <strong class="block text-sm font-bold mb-1 text-green-500">Keep Going!</strong>
          <p class="text-xs text-gray-500 text-justify">
            You don't have a Scopus ID listed yet, but every step you take in your research brings
            you closer to achieving it. If you have a Scopus ID, please update your profile and add
            it to enhance your academic visibility. Keep up the great work!
          </p>
        </div>
      </div>
      <div
        *ngIf="!isOwnProfile"
        class="w-full md:w-2/5 flex flex-col justify-center items-center gap-x-4 gap-y-1"
      >
        <div class="w-full px-3 text-center">
          <strong class="block text-sm font-bold mb-1 text-green-500"
            >Encourage Profile Completion</strong
          >
          <p class="text-xs text-gray-500 text-justify">
            {{ user.first_name }} {{ user.last_name }} does not have a Scopus ID listed yet. Having
            a Scopus ID can significantly increase academic visibility and make it easier for others
            to find and cite their work. If you know {{ user.first_name }}, please encourage them to
            add their Scopus ID to their profile to enhance their academic presence.
          </p>
        </div>
      </div>
    </ng-template>
  </div>
  <button
    *ngIf="isOwnProfile"
    class="absolute top-4 right-4 md:right-8 flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm group"
    (click)="toggleForm()"
  >
    <svg
      class="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-700 transition-colors duration-200"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      ></path>
    </svg>
    <span class="font-medium">Edit Profile</span>
  </button>
  <div
    *ngIf="showForm"
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75"
  >
    <app-data-form
      [user]="user"
      (formSubmitted)="toggleForm()"
      (formClosed)="toggleForm()"
    ></app-data-form>
  </div>
</div>

<div
  *ngIf="!isLoading && authorCentinela && isAuthor"
  class="max-w-6xl mx-auto flex flex-col lg:flex-row items-start gap-6 p-4 sm:px-6 lg:px-8 relative"
>
  <!-- MAIN COLUMN (Left) -->
  <div
    class="lg:w-2/3 w-full flex flex-col gap-6 bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
  >
    <!-- Header: Photo + Name/Affil -->
    <div
      class="flex flex-col sm:flex-row gap-6 items-center sm:items-start border-b border-gray-100 pb-6"
    >
      <!-- Profile Picture -->
      <div
        class="relative w-32 h-32 overflow-hidden rounded-full border-4 border-red-50 shadow-md ring-4 ring-gray-50 flex-shrink-0"
      >
        <img
          class="object-cover w-full h-full"
          [src]="'./assets/profile.png'"
          alt="Profile Picture"
        />
      </div>

      <!-- Core Info -->
      <div class="text-center sm:text-left flex-1">
        <h1 class="text-3xl font-extrabold text-gray-900 leading-tight mb-2">
          {{ authorCentinela.first_name }} {{ authorCentinela.last_name }}
        </h1>

        <div *ngIf="authorCentinela.current_affiliation" class="mt-4">
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800"
          >
            <svg
              class="w-3.5 h-3.5 mr-1.5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              ></path>
            </svg>
            Affiliation
          </span>
          <p class="text-sm text-gray-700 mt-2 font-medium">
            {{ authorCentinela.current_affiliation }}
          </p>
        </div>

        <div *ngIf="authorCentinela.email" class="mt-3">
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800"
          >
            <svg
              class="w-3.5 h-3.5 mr-1.5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>
            Email
          </span>
          <p class="text-sm text-gray-700 mt-2">{{ authorCentinela.email }}</p>
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div class="pt-2">
      <h2 class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
        Articles Timeline
      </h2>

      <!-- Loaded Chart -->
      <div
        *ngIf="years && charged"
        class="w-full flex justify-center items-center bg-gray-50 rounded-xl border border-gray-100 p-4"
      >
        <div class="max-h-fit" *ngIf="years[0].series.length > 1">
          <app-evolution-line-chart
            [multi]="years"
            [width]="600"
            [height]="250"
            [boolean]="true"
          ></app-evolution-line-chart>
        </div>
        <div *ngIf="years[0].series.length <= 1" class="text-gray-500 text-sm text-center py-10">
          Not enough data to display chart.
        </div>
      </div>

      <!-- Error State -->
      <div
        *ngIf="chartError"
        class="w-full flex flex-col justify-center items-center bg-gray-50 rounded-xl border border-gray-200 border-dashed p-8 text-center"
      >
        <svg
          class="w-10 h-10 text-gray-400 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          ></path>
        </svg>
        <p class="text-gray-600 font-medium">Timeline data is temporarily unavailable.</p>
        <p class="text-sm text-gray-500 mt-1">
          This will be populated once connected to the metrics service.
        </p>
      </div>
    </div>
  </div>

  <!-- SIDEBAR COLUMN (Right) -->
  <div class="lg:w-1/3 flex flex-col gap-6">
    <!-- SCOPUS BUTTON CARD -->
    <div
      class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center"
    >
      <div
        class="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-4 p-2 shadow-sm border border-gray-100"
      >
        <img
          src="assets/icons/scopus.svg"
          onerror="
            this.src = 'assets/scopus.png';
            this.onerror = null;
          "
          alt="Scopus Logo"
          class="w-full h-full object-contain"
        />
      </div>
      <h3 class="text-lg font-bold text-gray-900 mb-2">View Original Profile</h3>
      <p class="text-sm text-gray-500 mb-6">
        Explore the complete author profile directly on Scopus.
      </p>
      <button
        (click)="goToScopus(authorCentinela.scopus_id)"
        class="w-full bg-red-700 hover:bg-red-800 text-white font-bold py-3.5 px-4 rounded-xl shadow-md transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center gap-2 focus:ring-4 focus:ring-red-200"
      >
        <span>View in Scopus</span>
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          ></path>
        </svg>
      </button>
    </div>

    <!-- DETAILS CARD (Metrics) -->
    <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
      <h2
        class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2"
      >
        Author Metrics
      </h2>
      <div class="flex flex-col gap-4">
        <div>
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Citations</p>
          <p class="text-sm text-gray-900 font-medium flex items-center gap-2">
            <span
              class="bg-red-50 text-red-700 text-xs font-bold px-2 py-0.5 rounded-md border border-red-100"
              >{{ authorCentinela.citation_count || 0 }}</span
            >
          </p>
        </div>
        <div>
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Articles</p>
          <p class="text-sm text-gray-900 font-medium flex items-center gap-2">
            <span class="bg-gray-100 text-gray-800 text-xs font-bold px-2.5 py-1 rounded-md">{{
              authorCentinela.articles || 0
            }}</span>
          </p>
        </div>
        <div>
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Scopus ID</p>
          <p class="text-sm text-gray-900 font-medium break-all">{{ authorCentinela.scopus_id }}</p>
        </div>
      </div>
    </div>
  </div>
  <button
    *ngIf="isOwnProfile"
    class="absolute top-4 right-4 md:right-8 flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm group"
    (click)="toggleForm()"
  >
    <svg
      class="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-700 transition-colors duration-200"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      ></path>
    </svg>
    <span class="font-medium">Edit Profile</span>
  </button>
  <div
    *ngIf="showForm"
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75"
  >
    <app-data-form
      [user]="user"
      (formSubmitted)="toggleForm()"
      (formClosed)="toggleForm()"
    ></app-data-form>
  </div>
</div>
